// src/pages/admin/Invoices.tsx
//
// Admin Invoices pane — live list from the imported FileMaker
// ledger. Click a row to open the per-invoice lines panel (line
// count, product, qty, unit/total, line image thumbnail). Year and
// search filters hit the server; debounced 200ms to keep API chatter
// down on each keystroke.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, ChevronRight, Receipt } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { EmptyState } from "@/components/ui/EmptyState";
import { useAuth } from "@/components/auth/AuthContext";
import { authHeaders } from "@/lib/api";
import { formatPrice } from "@/lib/formatPrice";
import { InvoiceDetailPanel } from "@/components/feature/InvoiceDetailPanel";

interface InvoiceRow {
  invoice_number: string;
  invoice_date: string | null;
  customer_id: number | null;
  customer_name_snapshot: string | null;
  payment_method: string | null;
  vat_exemption: string | null;
  total_htva: number | string | null;
  total_tva: number | string | null;
  total_tvac: number | string | null;
  total_discount_htva: number | string | null;
  line_count: number;
}

export function AdminInvoices() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [years, setYears] = useState<number[]>([]);
  const [year, setYear] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openFor, setOpenFor] = useState<string | null>(null);

  // available years (real invoice ledger)
  useEffect(() => {
    if (!auth.isReady || !auth.isAuthenticated) return;
    fetch("/api/sales/years", { headers: authHeaders() })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = (await r.json()) as { years: { year: number }[] };
        const ys = body.years.map((y) => y.year);
        setYears(ys);
        if (ys.length > 0) setYear((y) => y ?? (ys[0] ?? null));
      })
      .catch((e) => setError(String(e instanceof Error ? e.message : e)));
  }, [auth.isReady, auth.isAuthenticated]);

  useEffect(() => {
    if (year == null) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    const handle = setTimeout(() => {
      const url = new URL("/api/admin/invoices", window.location.origin);
      if (year) url.searchParams.set("year", String(year));
      if (query) url.searchParams.set("q", query);
      url.searchParams.set("limit", "100");
      fetch(url.toString().replace(window.location.origin, ""), {
        headers: authHeaders(),
      })
        .then(async (r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const body = (await r.json()) as { rows: InvoiceRow[]; total: number };
          if (!cancelled) {
            setRows(body.rows);
            setTotal(body.total);
          }
        })
        .catch((e) => {
          if (!cancelled) setError(String(e instanceof Error ? e.message : e));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [year, query]);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-xl font-510 tracking-tight text-fg-primary">
            <Receipt className="mr-2 inline h-5 w-5 text-benchmark-teal" strokeWidth={1.5} />
            {t("admin.invoices.title")}
          </h1>
          <p className="mt-1 text-sm text-fg-tertiary">
            {t("admin.invoices.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-fg-tertiary">{t("admin.invoices.year")}</span>
          <select
            value={year ?? ""}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-md border border-border bg-bg-elevated px-2 py-1 text-sm text-fg-primary"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-tertiary"
              strokeWidth={1.5}
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("admin.invoices.searchPlaceholder")}
              className="pl-8"
              aria-label={t("admin.invoices.searchAria")}
            />
          </div>
          <p className="text-[11px] text-fg-quaternary">
            {t("admin.invoices.matchCount", {
              value: total.toLocaleString(),
            })}{" "}
            {t("admin.invoices.shown", { count: rows.length })}
            {query ? ` · "${query}"` : ""}
          </p>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
          {error}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-fg-tertiary">
          {t("admin.invoices.loading")}
        </p>
      ) : rows.length === 0 && !error ? (
        <EmptyState
          title={t("admin.invoices.emptyTitle")}
          description={t("admin.invoices.emptyDescription")}
        />
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const open = openFor === r.invoice_number;
            return (
              <Card key={r.invoice_number} className="overflow-hidden">
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={`lines-${r.invoice_number}`}
                  onClick={() => setOpenFor((cur) => cur === r.invoice_number ? null : r.invoice_number)}
                  className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-bg-hover"
                >
                  <ChevronRight
                    className={"h-3.5 w-3.5 text-fg-tertiary transition-transform " + (open ? "rotate-90" : "")}
                    strokeWidth={1.5}
                  />
                  <code className="rounded bg-bg-elevated px-1.5 py-0.5 font-mono text-[10px] text-fg-secondary">
                    {r.invoice_number}
                  </code>
                  <span className="text-sm text-fg-primary">{r.customer_name_snapshot ?? "—"}</span>
                  <Badge variant="default" size="sm">
                    {r.line_count === 1
                      ? t("admin.invoices.lineCount", { count: r.line_count })
                      : t("admin.invoices.lineCountOther", { count: r.line_count })}
                  </Badge>
                  {r.payment_method && (
                    <Badge variant="default" size="sm">{r.payment_method}</Badge>
                  )}
                  <span className="ml-auto font-mono text-sm font-510 text-fg-primary">
                    {formatPrice(Number(r.total_tvac ?? 0), "EUR")}
                  </span>
                  <span className="text-[10px] text-fg-quaternary w-20 text-right">
                    {r.invoice_date ?? ""}
                  </span>
                </button>
                {open && <InvoiceDetailPanel invoiceNumber={r.invoice_number} />}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
